import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../services/apiBase';

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [usageStats, setUsageStats] = useState(null);
  const [dataStatus, setDataStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      console.debug('AdminDashboard.fetchData apiBaseUrl=', apiBaseUrl);

      const [usageResponse, statusResponse] = await Promise.all([
        fetch(`${apiBaseUrl}/admin/usage-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${apiBaseUrl}/admin/data-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!usageResponse.ok) {
        let body = '';
        try { body = await usageResponse.text(); } catch (e) { body = '<unreadable>'; }
        console.error('AdminDashboard usage-stats failed', usageResponse.status, usageResponse.statusText, body);
      } else {
        setUsageStats(await usageResponse.json());
      }

      if (!statusResponse.ok) {
        let body = '';
        try { body = await statusResponse.text(); } catch (e) { body = '<unreadable>'; }
        console.error('AdminDashboard data-status failed', statusResponse.status, statusResponse.statusText, body);
      } else {
        setDataStatus(await statusResponse.json());
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">System usage and data status</p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/admin/conversation-review')}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Review Conversations
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Back to Chat
              </button>
              <button
                onClick={() => navigate('/guide')}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Guide
              </button>
              <button
                onClick={() => { logout(); navigate('/login'); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        {usageStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600">{usageStats.summary.total_users}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900">Total Cost</h3>
              <p className="text-3xl font-bold text-green-600">${usageStats.summary.total_cost.toFixed(4)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900">Total Sessions</h3>
              <p className="text-3xl font-bold text-purple-600">{usageStats.summary.total_sessions}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900">Daily Cap</h3>
              <p className="text-3xl font-bold text-orange-600">${usageStats.summary.daily_cap_per_user}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User Usage Stats */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">User Usage Statistics</h2>
            </div>
            <div className="p-6">
              {usageStats?.users?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Today</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {usageStats.users.map((user, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.role.replace('_', ' ')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${user.total_cost.toFixed(4)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">${user.today_cost.toFixed(4)}</div>
                            <div className="text-xs text-gray-500">${user.today_remaining.toFixed(4)} left</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.last_login)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No user data available</p>
              )}
            </div>
          </div>

          {/* Data Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Data Status</h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Analytics Status */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Analytics</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Sites:</span>
                      <span className="ml-2 font-medium">{dataStatus?.analytics?.site_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Revenue:</span>
                      <span className="ml-2 font-medium">${dataStatus?.analytics?.total_revenue?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">Last Update:</span>
                    <span className="ml-2 text-sm">{formatDate(dataStatus?.analytics?.last_update)}</span>
                  </div>
                  {dataStatus?.analytics?.error && (
                    <div className="mt-2 text-sm text-red-600">Error: {dataStatus.analytics.error}</div>
                  )}
                </div>
              </div>

              {/* RAG Index Status */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">RAG Index</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Indexed Sites:</span>
                      <span className="ml-2 font-medium">{dataStatus?.rag_index?.indexed_sites || 0}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Saved Sites:</span>
                      <span className="ml-2 font-medium">{dataStatus?.saved_sites || 0}</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">Last Update:</span>
                    <span className="ml-2 text-sm">{formatDate(dataStatus?.rag_index?.last_update)}</span>
                  </div>
                  {dataStatus?.rag_index?.error && (
                    <div className="mt-2 text-sm text-red-600">Error: {dataStatus.rag_index.error}</div>
                  )}
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Last checked: {formatDate(dataStatus?.last_checked)}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;